import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  INLINE_PANEL_MAX_WIDTH,
  INLINE_PANEL_MIN_WIDTH,
} from '@/hooks/useCanHostInlinePanel';

/**
 * Drag-resize for the chat page's docked proposal pane.
 *
 * Same drag mechanics as `useReasoningSidebarPanel.handleResizeStart` (refs
 * for drag state, rAF-batched updates, body cursor/select feedback) but in
 * PIXELS, writing into the shared `SlideoutWidthContext` setters the caller
 * passes in — the same width the builder record panel and the floating
 * composer read, so the pane, the chat column, and the composer offset all
 * move together. Clamped to the shared inline-panel bounds
 * (`useCanHostInlinePanel`), the same range ActionDetailPage's SplitView
 * resize allows.
 */

export interface UseProposalPaneResizeResult {
  onResizeStart: (e: ReactMouseEvent) => void;
  /**
   * Adjust the width by a signed pixel delta (positive grows), clamped to
   * the inline-panel bounds. Shaped for EdgeResizeHandle's onKeyboardResize,
   * which owns the arrow-key → delta mapping.
   */
  resizeBy: (delta: number) => void;
}

export function useProposalPaneResize(
  setWidth: (width: number) => void,
  setIsResizing: (resizing: boolean) => void,
  currentWidth: number
): UseProposalPaneResizeResult {
  const [dragging, setDragging] = useState(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(currentWidth);

  const onResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();

      dragStartXRef.current = e.clientX;
      dragStartWidthRef.current = currentWidth;
      setDragging(true);
      setIsResizing(true);

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.body.classList.add('is-resizing-sidebar');
    },
    [currentWidth, setIsResizing]
  );

  useEffect(() => {
    if (!dragging) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      // Pane sits on the right: dragging left grows it.
      const deltaX = dragStartXRef.current - e.clientX;
      const next = Math.min(
        Math.max(dragStartWidthRef.current + deltaX, INLINE_PANEL_MIN_WIDTH),
        INLINE_PANEL_MAX_WIDTH
      );
      requestAnimationFrame(() => {
        setWidth(next);
      });
    };

    const handleMouseUp = () => {
      setDragging(false);
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('is-resizing-sidebar');
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, setWidth, setIsResizing]);

  const resizeBy = useCallback(
    (delta: number) => {
      setWidth(
        Math.min(
          Math.max(currentWidth + delta, INLINE_PANEL_MIN_WIDTH),
          INLINE_PANEL_MAX_WIDTH
        )
      );
    },
    [currentWidth, setWidth]
  );

  return { onResizeStart, resizeBy };
}
